import type { ErrorEvent, EventHint } from '@sentry/react';

/**
 * Keep the messages the app deliberately shows a user out of Sentry.
 *
 * Every async thunk here ends its failure path the same way: show the user a
 * notification, then `rejectWithValue(message)` with a plain string. Any such
 * rejection that isn't caught at the dispatch site reaches the browser as an
 * unhandled rejection, and Sentry files it as
 * "Non-Error promise rejection captured with value: <the message>".
 *
 * Those are not faults. "You can't raise a request on behalf of yourself" is
 * the form telling someone to correct a field — the user saw it, acted on it,
 * and nothing is broken. Reported as exceptions they bury the events that do
 * mean something.
 *
 * Deliberately narrow: only rejections whose value is a string are dropped,
 * because that is what `rejectWithValue` produces here. A genuine bug rejects
 * with an Error, and a rejection carrying an object or a response still comes
 * through — those are worth seeing.
 */
export const dropHandledRejections = (
    event: ErrorEvent,
    hint?: EventHint,
): ErrorEvent | null => {
    if (typeof hint?.originalException === 'string') {
        return null;
    }

    return event;
};

export default dropHandledRejections;
